/**
 * Active role, as it travels with a permission-gated request.
 *
 * The user works under exactly one role, chosen at `/additional-info`. A permission-gated
 * request carries that role's name so the backend checks the permission against **that**
 * role's grants rather than the union of every role the account holds. Requests made before a
 * role is selected (login, the `/additional-info` lookups themselves) carry no header and only
 * reach routes that are not permission gated.
 *
 * Requests that are never permission gated must not carry it either: the session API
 * (`/session/register`, `/session/refresh`), health checks and login. Those go to the auth
 * service, which has no use for the role, and on a deployed environment they are cross-origin,
 * so the browser preflights them. A header missing from that service's
 * `Access-Control-Allow-Headers` fails the preflight, `restoreUserSession()` reads the network
 * error as an invalid token, and the user is logged out on every page refresh. See
 * `shouldSendActiveRole`.
 *
 * HTTP takes it as the `x-role` header. Socket.IO takes it twice: in the handshake `auth`
 * payload and in `extraHeaders`. Both are sent because only `auth` is reliable — a browser
 * cannot set custom headers on a WebSocket handshake, so `extraHeaders` reaches the server
 * only on a polling transport, and every Echo namespace is configured `transports:
 * ["websocket"]`. The server must read `handshake.auth.role`, the same way it already reads
 * `handshake.auth.token`.
 */

export const ACTIVE_ROLE_HEADER = "x-role";

/** The shape stored in `useAdditionalInfo.role`. */
export interface ActiveRole {
  label?: string | null;
  value?: string | null;
}

/**
 * Get the `x-role` value for the selected role
 * @param role The role selected at `/additional-info`, if any
 * @returns The role name, or undefined when no role is selected
 */
export const getActiveRoleHeaderValue = (
  role: ActiveRole | null | undefined,
): string | undefined => {
  const label = typeof role?.label === "string" ? role.label.trim() : "";
  return label.length > 0 ? label : undefined;
};

/**
 * Add `x-role` to a request's headers when a role is selected
 * @param headers Headers object to mutate
 * @param role The role selected at `/additional-info`, if any
 * @returns The same headers object, for chaining
 */
export const applyActiveRoleHeader = <T extends Record<string, unknown>>(
  headers: T,
  role: ActiveRole | null | undefined,
): T => {
  const value = getActiveRoleHeaderValue(role);
  if (value) {
    (headers as Record<string, unknown>)[ACTIVE_ROLE_HEADER] = value;
  }
  return headers;
};

/** The request classification the interceptor has already derived from the URL. */
export interface ActiveRoleRequestKind {
  isSessionApiRequest: boolean;
  isHealthCheck: boolean;
  isLoginRequest: boolean;
}

/**
 * Decide whether a request should carry the active role
 *
 * Only permission-gated business requests do. The session API, health checks and login are not
 * permission gated, and sending `x-role` to the auth service breaks its CORS preflight.
 * @param kind The request classification derived from the URL
 * @returns True when the request should carry `x-role`
 */
export const shouldSendActiveRole = (kind: ActiveRoleRequestKind): boolean =>
  !kind.isSessionApiRequest && !kind.isHealthCheck && !kind.isLoginRequest;

/** Handshake options carrying the active role to a Socket.IO namespace. */
export interface ActiveRoleSocketOptions {
  auth: Record<string, unknown>;
  extraHeaders?: Record<string, string>;
}

/**
 * Add the active role to a Socket.IO handshake `auth` payload
 * @param auth The payload the namespace already sends, typically `{ token }`
 * @param role The role selected at `/additional-info`, if any
 * @returns A new payload, unchanged when no role is selected
 */
export const buildActiveRoleAuth = (
  auth: Record<string, unknown>,
  role: ActiveRole | null | undefined,
): Record<string, unknown> => {
  const value = getActiveRoleHeaderValue(role);
  return value ? { ...auth, role: value } : { ...auth };
};

/**
 * Build the Socket.IO handshake options that carry the active role
 * @param auth The payload the namespace already sends, typically `{ token }`
 * @param role The role selected at `/additional-info`, if any
 * @returns `auth`, plus `extraHeaders` only when a role is selected
 */
export const buildActiveRoleSocketOptions = (
  auth: Record<string, unknown>,
  role: ActiveRole | null | undefined,
): ActiveRoleSocketOptions => {
  const value = getActiveRoleHeaderValue(role);
  if (!value) {
    return { auth: { ...auth } };
  }
  return { auth: { ...auth, role: value }, extraHeaders: { [ACTIVE_ROLE_HEADER]: value } };
};
